import React, { useState } from 'react';
import { Box, Typography, Stack, Tabs, Tab, Paper, Chip, Link, Button } from '@mui/material';
import { CheckCircleOutline, InfoOutlined, DesktopWindows, VpnKeyOffOutlined, Wifi, ChromeReaderMode } from '@mui/icons-material';
import CommonWrapper from 'Components/Common/CommonWrapper';
import SessionFAQ from './SessionFAQ';

// Helper component for the light blue section headers
const SectionHeader = ({ icon, title, chipLabel, chipColor = "default" }) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={1.5}
    sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2 }}
  >
    {icon}
    <Typography variant="body1" fontWeight="bold" color="secondary.main" sx={{ flexGrow: 1 }}>
      {title}
    </Typography>
    {chipLabel && (
        <Chip 
            label={chipLabel} 
            variant="outlined" 
            color={chipColor} 
            size="small" 
            sx={{ bgcolor: 'transparent', height: 'auto', '& .MuiChip-label': { px: 1 } }} 
        />
    )}
  </Stack>
);

// Helper for custom bullet points to ensure correct alignment and styling
const BulletPoint = ({ children }) => (
  <Stack direction="row" spacing={1.5} component="li" sx={{ alignItems: 'flex-start' }}>
    <Typography component="span" sx={{ color: 'secondary.main', lineHeight: '24px' }}>•</Typography>
    <Typography variant="body1" color="secondary.light" component="div">{children}</Typography>
  </Stack>
);

// A reusable component for the complex "No Show Policy" section
const NoShowPolicy = () => (
  <Stack spacing={2.5} sx={{ mt: 2 }}>
    <Stack spacing={1}>
      <Typography variant="body1" fontWeight="bold" color="secondary.main">General Guidelines</Typography>
      <Stack component="ul" sx={{ p: 0, m: 0, listStyle: 'none' }} spacing={1}>
        <BulletPoint>You are advised to join the session 5 mins before the scheduled time to avoid any delays or no show.</BulletPoint>
        <BulletPoint>If you fail to join the session on time the session will be marked as “NO SHOW” and you'll lose the chance to connect with top rated mentors.</BulletPoint>
        <BulletPoint>Repetitive no shows might debarr you from the interview process.</BulletPoint>
      </Stack>
    </Stack>
    <Stack spacing={1}>
      <Typography variant="body1" fontWeight="bold" color="secondary.main">What Happens if there is a NO SHOW?</Typography>
      <Stack component="ul" sx={{ p: 0, m: 0, listStyle: 'none' }} spacing={1}>
        <BulletPoint>The first NO SHOW will lead to <Typography component="span" color="error.main">losing your expert mock Interview session</Typography></BulletPoint>
        <BulletPoint>The second NO SHOW will lead to <Typography component="span" color="error.main">deduction of 1 additional Expert Mock Interview Session</Typography></BulletPoint>
      </Stack>
    </Stack>
    <Stack spacing={1}>
      <Typography variant="body1" fontWeight="bold" color="secondary.main">What Happens if there is a Mentor NO SHOW?</Typography>
      <Stack component="ul" sx={{ p: 0, m: 0, listStyle: 'none' }} spacing={1}>
        <BulletPoint>For the first Mentor NO SHOW, we will compensate you by <Typography component="span" color="success.main">adding <Box component="span" sx={{ display: 'inline-block', width: 12, height: 12, borderRadius: '50%', bgcolor: 'yellow.main', mx: 0.5, verticalAlign: 'middle' }} />10 coins</Typography> to your account</BulletPoint>
        <BulletPoint>From the second Mentor NO SHOW, we will compensate you by <Typography component="span" color="success.main">adding a bonus expert mock Interview session</Typography></BulletPoint>
      </Stack>
    </Stack>
  </Stack>
);

// Main Component
const GuidelinesPage = () => {
  const [tabIndex, setTabIndex] = useState(1);

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  return (
    <CommonWrapper width="lg">
        <Box>
        <Stack spacing={1}>
            {/* Header */}
            <Stack direction="column" spacing={0.5}>
                <Typography variant="body2" color="secondary.light">EXPERT MOCK INTERVIEW</Typography>
                <Typography variant="h6" fontWeight="bold">Session Guidelines and FAQ's</Typography>
            </Stack>

            {/* Tabs */}
            <Box>
            <Tabs value={tabIndex} onChange={handleTabChange} TabIndicatorProps={{ hidden: true }} type="button">
                <Tab value={1} label="General Guidelines" />
                <Tab value={2} label="FAQ's" />
            </Tabs>
            </Box>

            {/* Content for "General Guidelines" Tab */}
            {tabIndex === 1 && (
                <Stack spacing={3}>
                    {/* Yellow Box Section */}
                    <Paper elevation={0} sx={{ p: 3, bgcolor: 'yellow.main', borderRadius: 2 }}>
                    <Stack spacing={2}>
                        <Box>
                        <Typography variant="body1" fontWeight="bold" color="secondary.main" mb={1}>
                            Why should I take these interviews?
                        </Typography>
                        <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 'none' }}>
                            <Typography variant="body1" color="secondary.light">
                            This session will help you understand your weak areas providing you timely feedback and all of this in an 1:1 interaction. It will also help you gain confidence which will reflect on your non-verbal and verbal communication abilities.
                            </Typography>
                        </Paper>
                        </Box>
                        <Box>
                        <Typography variant="body1" fontWeight="bold" color="secondary.main" mb={1}>
                            How these interviews will impact your career?
                        </Typography>
                        <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 'none' }}>
                            <Typography variant="body1" color="secondary.light">
                            On clearing a Mock Interview for any module you'll unlock multiple job opportunities and get referred to top companies by Scaler on priority.
                            </Typography>
                        </Paper>
                        </Box>
                    </Stack>
                    </Paper>

                    {/* Main Content Sections */}
                    <Stack spacing={2}>
                    <Box>
                        <SectionHeader icon={<CheckCircleOutline color="primary" />} title="Incentives for getting certified" />
                        <Stack component="ul" sx={{ p: 0, pl: 2, m: 0, mt: 2, listStyle: 'none' }} spacing={1}>
                        <BulletPoint>Unlock <Typography component="span" fontWeight="bold" color="secondary.main">skill certificates</Typography> 🏆 from Scaler upon clearing your mock interviews.</BulletPoint>
                        <BulletPoint>Earn upto <Typography component="span" fontWeight="bold" color="secondary.main">250 Coded coins</Typography> <Box component="span" sx={{ display: 'inline-block', width: 12, height: 12, borderRadius: '50%', bgcolor: 'yellow.main', verticalAlign: 'middle' }} /> for each of the cleared mock interview.</BulletPoint>
                        </Stack>
                    </Box>

                    <Box>
                        <SectionHeader icon={<CheckCircleOutline color="primary" />} title="Reschedules Policies" chipLabel="2/2 Rescheduling Left" />
                        <Stack component="ul" sx={{ p: 0, pl: 2, m: 0, mt: 2, listStyle: 'none' }} spacing={1}>
                            <BulletPoint>Please reschedule only in case you are unavailable at the scheduled time.</BulletPoint>
                            <BulletPoint>You can only reschedule upto <Box component="span" sx={{bgcolor: 'rgba(255, 221, 161, 0.5)', px: 0.5, borderRadius: '4px'}}>2 sessions / 2 months</Box>.</BulletPoint>
                            <BulletPoint>You can reschedule the session 3 hours before the scheduled time.</BulletPoint>
                        </Stack>
                    </Box>

                    <Box>
                        <SectionHeader icon={<CheckCircleOutline color="primary" />} title="Cancellation Policy" chipLabel="New Policy" chipColor="primary" />
                        <Stack component="ul" sx={{ p: 0, pl: 2, m: 0, mt: 2, listStyle: 'none' }} spacing={1}>
                            <BulletPoint>Cancellations are only allowed in emergencies.</BulletPoint>
                            <BulletPoint>To cancel a session, submit a cancellation request with your reason.</BulletPoint>
                            <BulletPoint>You can only cancel up-to <Typography component="span" color="error.main">1 sessions / 12 months</Typography>.</BulletPoint>
                            <BulletPoint>You can cancel the session only from <Typography component="span" fontWeight="bold" color="secondary.main">12 hours to 30 minutes before</Typography> the session start time.</BulletPoint>
                        </Stack>
                    </Box>

                    <Box>
                        <SectionHeader icon={<CheckCircleOutline color="primary" />} title="No Show Policies" />
                        <Box sx={{pl: 2}}>
                        <NoShowPolicy />
                        </Box>
                    </Box>

                    <Box>
                        <SectionHeader icon={<CheckCircleOutline color="primary" />} title="Pre-Requisite for better audio/video experience" />
                        <Box sx={{pl: 2}}>
                        <NoShowPolicy />
                        <Stack direction="row" spacing={2} mt={2} flexWrap="wrap" useFlexGap>
                            <Button variant="outlined" size="small" sx={{ color: 'secondary.light', borderColor: 'secondary.light' }} startIcon={<DesktopWindows />}>Desktop/Laptop Recommended</Button>
                            <Button variant="outlined" size="small" sx={{ color: 'secondary.light', borderColor: 'secondary.light' }} startIcon={<VpnKeyOffOutlined />}>Close Display VPN or Proxy Setting</Button>
                            <Button variant="outlined" size="small" sx={{ color: 'secondary.light', borderColor: 'secondary.light' }} startIcon={<Wifi />}>{`Stable network with >500kbps speed`}</Button>
                            <Button variant="outlined" size="small" sx={{ color: 'secondary.light', borderColor: 'secondary.light' }} startIcon={<ChromeReaderMode />}>For better experience switch to Chrome</Button>
                        </Stack>
                        <Typography variant="body2" color="secondary.light" mt={2}>For more information on joining issues and fixes <Link href="#">Click here</Link></Typography>
                        </Box>
                    </Box>
                    
                    <Box>
                        <SectionHeader icon={<CheckCircleOutline color="primary" />} title="How Feedback is shared with you" />
                        <Stack spacing={2} sx={{mt: 2}}>
                            <Typography variant="body1" color="secondary.main">Once the session is completed, your interviewer gives you any of the following depending on how you performed during the interview :</Typography>
                            <Stack direction="row" spacing={1} alignItems="flex-start">
                                <InfoOutlined sx={{color: 'text.secondary', mt: 0.5}}/>
                                <Typography variant="body1" color="secondary.light">Weak hire will not affect your placement journey. You will be able to give this interview again from your <Link href="#">Additional Expert Mock Interview</Link> and can convert it to Strong hire</Typography>
                            </Stack>
                            <Stack spacing={2}>
                                <Paper variant="outlined" display="flex" flex sx={{ p: 2, borderColor: 'border.grey' }}>
                                    <Typography variant="body1" fontWeight="bold" color="secondary.main" mb={1}>Strong Hire</Typography>
                                    <Typography variant="body1" color="secondary.light">You've been given a strong hire rating by the expert interviewer, this means that you have high chances of clearing interviews when you start interviewing for opportunities.</Typography>
                                </Paper>
                                <Paper variant="outlined" sx={{ p: 2, borderColor: 'border.grey' }}>
                                    <Typography variant="body1" fontWeight="bold" color="secondary.main" mb={1}>Weak Hire</Typography>
                                    <Typography variant="body1" color="secondary.light">You've been given a weak hire rating by the expert interviewer, this means that there's a small chance that some companies might drop you if they have a better option available. However, you can still work hard and improve your skills using the detailed feedback.</Typography>
                                </Paper>
                                <Paper variant="outlined" sx={{ p: 2, borderColor: 'border.grey' }}>
                                    <Typography variant="body1" fontWeight="bold" color="secondary.main" mb={1}>Weak Reject</Typography>
                                    <Typography variant="body1" color="secondary.light">You've been given a weak reject rating by the expert interviewer, this means that you're just one step away from being a high priority candidate in companies. You can go through the concepts again and can appear for the interview</Typography>
                                </Paper>
                                <Paper variant="outlined" sx={{ p: 2, borderColor: 'border.grey' }}>
                                    <Typography variant="body1" fontWeight="bold" color="secondary.main" mb={1}>Strong Reject</Typography>
                                    <Typography variant="body1" color="secondary.light">You've been given a strong reject rating by the expert interviewer, this means that you need to work hard on your skills so that you can start appearing for company interviews. Go through the detailed feedback shared, work on your skills and appear for the interview again</Typography>
                                </Paper>
                            </Stack>
                        </Stack>
                    </Box>
                    </Stack>

                    {/* Footer */}
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{mt: 4, flexWrap: 'wrap', gap: 2}}>
                    <Typography variant="body1" color="secondary.light">Still looking for an answer? Connect with our team through</Typography>
                    <Link href="#" fontWeight="bold" sx={{textDecoration: 'none'}}>Scaler chat or call support</Link>
                    </Stack>
                </Stack>
            )}

            {/* Content for "FAQ" Tab */}
            {tabIndex === 2 && (
                <SessionFAQ />
            )}
        </Stack>
        </Box>
    </CommonWrapper>
  );
}

export default GuidelinesPage;